import {
  dateFormatterDayMonthYearLong2Digits,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  Spacer,
  StatusSeverity,
  TextCapitalized,
  usePersistMuiTableState,
} from "@qivia/ui";
import { useAppSelector } from "../../redux/hooks";
import styled from "styled-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import "../../i18n/i18n";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { formatDate, getDate } from "../../lib/materialUiFormats";
import {
  selectBadgesReturnedList,
  selectBadgesReturnedListStatus,
} from "./badgesSlice";
import { BadgesReturnedListDisplayed } from "./badgesApi";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useTranslation } from "react-i18next";
import { isOlderThanOneMonth } from "../lib";
import { selectCompaniesList } from "../companiesSlice";

export const BadgesReturnedTab = () => {
  const { t } = useTranslation();
  const params = useParams();
  const badgesReturnedList = useAppSelector(selectBadgesReturnedList);
  const badgesReturnedListStatus = useAppSelector(
    selectBadgesReturnedListStatus,
  );
  const companiesList = useAppSelector(selectCompaniesList);
  const [listDisplayed, setListDisplayed] = useState<
    (BadgesReturnedListDisplayed & { statusSeverity?: StatusSeverity })[]
  >([]);

  const pageName = "badges_returned";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      name: "size",
      returnedAt: "size",
      asfReturnReceivedAt: "size",
      informations: "size",
    },
  };

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const findKeysToTranslate = useCallback(
    (key: keyof BadgesReturnedListDisplayed, value: string | null) => {
      if (!value) return value;
      switch (key) {
        case "returnedAt":
        case "asfReturnReceivedAt":
          return dateFormatterDayMonthYearLong2Digits(new Date(value));
        default:
          return value;
      }
    },
    [],
  );

  const keysToTranslate = useMemo(
    () =>
      [
        "returnedAt",
        "asfReturnReceivedAt",
      ] as (keyof BadgesReturnedListDisplayed)[],
    [],
  );

  const badgesReturnedListWithId: (BadgesReturnedListDisplayed & {
    id: string;
    statusSeverity?: StatusSeverity;
  })[] = useMemo(() => {
    const companiesNames = companiesList.reduce(
      (acc, company) => {
        acc[company.uuid] = company.name;
        return acc;
      },
      {} as Record<string, string>,
    );
    return badgesReturnedList.map((badge) => {
      let newDict: BadgesReturnedListDisplayed & {
        id: string;
        statusSeverity?: StatusSeverity;
      } = {
        ...badge,
        id: badge.uuid,
        companyName: companiesNames[badge.companyUuid],
        statusSeverity:
          badge.asfReturnReceivedAt === null &&
          isOlderThanOneMonth(badge.returnedAt)
            ? "error"
            : undefined,
      };
      Object.entries(badge).forEach(([key, value]) => {
        if (
          keysToTranslate.includes(key as keyof BadgesReturnedListDisplayed)
        ) {
          newDict = {
            ...newDict,
            [key]: value
              ? findKeysToTranslate(
                  key as keyof BadgesReturnedListDisplayed,
                  value,
                )
              : "",
          };
        }
      });
      return newDict;
    });
  }, [badgesReturnedList, companiesList, findKeysToTranslate, keysToTranslate]);

  useEffect(() => {
    if (badgesReturnedListWithId && badgesReturnedListStatus === "success") {
      setListDisplayed(badgesReturnedListWithId);
    }
  }, [badgesReturnedListWithId, badgesReturnedListStatus]);

  const searchBarProps = {
    values: badgesReturnedListWithId,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["uuid" as const],
  };

  const columnsDisplayed: {
    [index in keyof BadgesReturnedListDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      companyName: {
        type: "string",
        width: 150,
      },
      name: {
        type: "string",
        width: 250,
      },
      returnedAt: {
        type: "date",
        width: 200,
        valueGetter: getDate,
        valueFormatter: formatDate,
      },
      asfReturnReceivedAt: {
        type: "date",
        width: 200,
        valueGetter: getDate,
        valueFormatter: formatDate,
      },
      informations: {
        type: "string",
        width: 200,
      },
    };
  }, []);

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  if (params.tab !== "returned") {
    return;
  }

  return (
    <StyledContainer>
      <StyledExplanationContainer>
        <Spacer y={0.5} />
        <TextCapitalized>
          {t("badges_returned.explanation.part1")}
        </TextCapitalized>
        <Spacer y={0.5} />
        <TextCapitalized>
          {t("badges_returned.explanation.part2")}
        </TextCapitalized>
        <Spacer y={2} />
        <TextCapitalized>
          {t("badges_returned.explanation.part3")}
        </TextCapitalized>
        <Spacer y={1} />
      </StyledExplanationContainer>
      <Spacer y={1} />
      <MaterialUiTable<
        keyof BadgesReturnedListDisplayed,
        BadgesReturnedListDisplayed
      >
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledExplanationContainer = styled.div`
  ${typographies["Body/XS"]};
`;
